import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

import { adminSend, rawSend } from "./api";
import type { SessionInfo } from "./types";

type Ceremony<T> = { challengeId: string; options: T };

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function signInWithPasskey(): Promise<SessionInfo> {
  const begin = await rawSend<Ceremony<PublicKeyCredentialRequestOptionsJSON>>("POST", "/admin/auth/sign-in/options");
  const response = await startAuthentication({ optionsJSON: begin.options });
  return rawSend<SessionInfo>("POST", "/admin/auth/sign-in/verify", { challengeId: begin.challengeId, response });
}

export async function enrollWithPasskey(token: string, name: string): Promise<SessionInfo> {
  const begin = await rawSend<Ceremony<PublicKeyCredentialCreationOptionsJSON> & { operator: { email: string; name: string } }>(
    "POST",
    "/admin/auth/enrollment/options",
    { token },
  );
  const response = await startRegistration({ optionsJSON: begin.options });
  return rawSend<SessionInfo>("POST", "/admin/auth/enrollment/verify", { token, challengeId: begin.challengeId, response, name });
}

export async function invitationFor(token: string) {
  return rawSend<{ operator: { email: string; name: string } }>("POST", "/admin/auth/enrollment/options", { token });
}

export async function reauthenticateWithPasskey(): Promise<SessionInfo> {
  const begin = await rawSend<Ceremony<PublicKeyCredentialRequestOptionsJSON>>("POST", "/admin/auth/reauthenticate/options");
  const response = await startAuthentication({ optionsJSON: begin.options });
  return rawSend<SessionInfo>("POST", "/admin/auth/reauthenticate/verify", { challengeId: begin.challengeId, response });
}

export async function addPasskey(name: string): Promise<{ id: string }> {
  const begin = await adminSend<Ceremony<PublicKeyCredentialCreationOptionsJSON>>("POST", "/admin/auth/passkeys/options");
  const response = await startRegistration({ optionsJSON: begin.options });
  return adminSend<{ id: string }>("POST", "/admin/auth/passkeys/verify", { challengeId: begin.challengeId, response, name });
}

export function passkeyCancelled(error: unknown): boolean {
  return error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");
}
